import { useContext, useEffect, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { PinGenerationStatus, ResponseContext } from "@swiggy-private/portal-dineout-context";
import { ingestPinInMenuResponse, isPinRenderedInMenu, shouldRenderPin } from "../utils/menu";

export const usePinInjection = () => {
    const { state, dispatch } = useContext(ResponseContext);
    const lastInjectedPinRef = useRef<string | null>(null);

    // Inject pin whenever pin data changes and menu data exists
    // This handles the case where pin arrives after menu is already loaded
    useEffect(() => {
        const hasPinData = Boolean(state.pin?.data?.status);
        // Key on userStatus too: a secondary user's status stays PIN_GENERATED_BY_OTHER_USER after
        // verifying (only userStatus flips to USER_VERIFIED), so the effect must re-fire on that change.
        const pinKey = `${state.pin?.data?.pin}-${state.pin?.data?.status}-${state.pin?.data?.userStatus}`;

        // A pin-less rewrite of initialData (refetch / fallback / cart update whose closure captured
        // empty pin data) can wipe an already-injected pin while leaving pin state unchanged. Detect
        // that "pin should show but isn't painted" case and re-inject even though pinKey is unchanged —
        // otherwise the dedup guard latches and the pin is dropped for the rest of the session.
        // Guard on a non-empty pin: an empty pin can never be painted into the masthead, so
        // isPinRenderedInMenu would always return false and this flag would latch true forever —
        // and since ingestPinInMenuResponse rewrites initialData (a dep of this effect) on every
        // run, that turns into an infinite re-injection loop (repeated menu rewrites / impressions).
        const pinDroppedFromMenu =
            hasPinData &&
            !isEmpty(state.pin?.data?.pin) &&
            shouldRenderPin(state.pin?.data?.status, state.pin?.data?.userStatus) &&
            !isPinRenderedInMenu(state.menu.initialData, state.pin?.data?.pin);

        // Inject pin if we have pin data and menu data, and haven't injected this pin yet
        // (or a menu rewrite dropped a pin we had already injected).
        if (hasPinData && state.menu.initialData && (lastInjectedPinRef.current !== pinKey || pinDroppedFromMenu)) {
            lastInjectedPinRef.current = pinKey;
            ingestPinInMenuResponse(
                state.menu.initialData,
                {
                    selectedTab: state.menu.parentType || "",
                    pin: state.pin.data.pin,
                    tableNo: state.pin.data.tableNo,
                    status: state.pin.data.status as PinGenerationStatus,
                    // Forward userStatus so a verified secondary user (status stays
                    // PIN_GENERATED_BY_OTHER_USER) gets the PIN injected top-right after pay-bill verify.
                    userStatus: state.pin.data.userStatus,
                },
                dispatch,
            );
        }

        // Reset pin injection flag when pin data is cleared
        if (!hasPinData) {
            lastInjectedPinRef.current = null;
        }
    }, [state.pin.data, state.menu.initialData, state.menu.parentType, dispatch]);
};
